/**
 * Environment Configuration
 *
 * Centralized access to all environment variables with type safety.
 * All VITE_ prefixed environment variables are public and safe to expose.
 */

/**
 * Validated environment configuration
 */
export const env = {
    // API Configuration
    API_URL: !import.meta.env.SSR && import.meta.env.VITE_CUSTOMER_MULTIDOMAIN === 'true'
        ? '/api'
        : import.meta.env.VITE_API_URL || '/api',
    CUSTOMER_MULTIDOMAIN: import.meta.env.VITE_CUSTOMER_MULTIDOMAIN === 'true',
    EN_SITE_URL: import.meta.env.VITE_CUSTOMER_ROUTING_ENVIRONMENT === 'staging' ? 'https://ie-test.tserentals.com' : 'https://storkrentals.ie',
    ES_SITE_URL: import.meta.env.VITE_CUSTOMER_ROUTING_ENVIRONMENT === 'staging' ? 'https://es-test.tserentals.com' : 'https://storkrentals.es',

    // NOTE: there is intentionally no global Stripe publishable key here.
    // Payments are multi-tenant: each order resolves its operating company's
    // publishable key at runtime from GET /customer/payments/config (and
    // per-payment overrides) — see app/contexts/StripeContext.tsx.

    // Google Analytics
    GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID || '',

    // Feature Flags
    FEATURE_REVIEWS: import.meta.env.VITE_FEATURE_REVIEWS === 'true',
    FEATURE_WISHLIST: import.meta.env.VITE_FEATURE_WISHLIST === 'true',

    // App Configuration
    APP_NAME: import.meta.env.VITE_APP_NAME || 'Stork Rentals',
    SUPPORT_EMAIL: import.meta.env.VITE_SUPPORT_EMAIL || 'info@thestorkexchange.ie',

    // SEO / Site
    // Canonical public origin (no trailing slash). Used for canonical tags,
    // Open Graph URLs, sitemap.xml and robots.txt.
    SITE_URL: (import.meta.env.VITE_SITE_URL || 'https://tserentals.com').replace(/\/+$/, ''),
    // When false (staging/preview), pages emit noindex and robots.txt blocks crawlers.
    SITE_INDEXABLE: import.meta.env.VITE_SITE_INDEXABLE !== 'false',

    // Preview gate ("Coming Soon" page) — off unless explicitly enabled.
    PREVIEW_GATE: import.meta.env.VITE_PREVIEW_GATE === 'true',
    PREVIEW_PASSWORD: import.meta.env.VITE_PREVIEW_PASSWORD || 'stork2026',

    // Build-time constants
    APP_VERSION: __APP_VERSION__,
    BUILD_TIME: __BUILD_TIME__,
    IS_PROD: __IS_PROD__,
    IS_DEV: import.meta.env.DEV,
    MODE: import.meta.env.MODE,
} as const;

/**
 * Validate required environment variables in production
 * Call this early in app initialization
 */
export function validateEnv(): void {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required in production
    if (env.IS_PROD) {
        if (!env.API_URL || (env.API_URL === '/api' && !env.CUSTOMER_MULTIDOMAIN)) {
            errors.push('VITE_API_URL is required in production');
        }

        // Stripe is multi-tenant: the publishable key for each order is
        // fetched at runtime per operating company (GET /customer/payments/config
        // + per-payment overrides). There is no global key to validate here.

        // Optional but recommended - warn if missing
        if (!env.GA_MEASUREMENT_ID) {
            warnings.push('VITE_GA_MEASUREMENT_ID not configured - analytics disabled');
        }
    }

    // Log warnings
    if (warnings.length > 0) {
        warnings.forEach(w => console.warn(`[ENV] ${w}`));
    }

    // Handle errors
    if (errors.length > 0) {
        console.error('Environment validation errors:', errors);
        if (env.IS_PROD) {
            throw new Error(`Invalid environment configuration:\n${errors.join('\n')}`);
        }
    }
}

/**
 * Get a feature flag value with fallback
 */
export function isFeatureEnabled(feature: 'reviews' | 'wishlist'): boolean {
    switch (feature) {
        case 'reviews':
            return env.FEATURE_REVIEWS;
        case 'wishlist':
            return env.FEATURE_WISHLIST;
        default:
            return false;
    }
}

// Declare compile-time constants
declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;
declare const __IS_PROD__: boolean;
